/**
 * Email Integration Settings Component
 *
 * Allows users to connect Gmail and Outlook accounts for
 * automatic email activity capture in the CRM.
 * Also supports IMAP/SMTP connections for any email provider.
 */

import React, { useState, useEffect } from 'react';
import {
  Mail,
  Plus,
  Settings,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Trash2,
  Loader2,
  Calendar,
  Link,
  Unlink,
  FolderOpen,
  Server,
  Eye,
  EyeOff,
  ArrowRight,
  Info,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

import { apiClient } from '@/services/apiClient';
import { imapAPI, type IMAPIntegration, type IMAPFolder } from '@/services/api/salesPipelineAPI';
import { useCapability } from '@/hooks/useCapabilities';

interface EmailIntegration {
  id: number;
  provider: 'gmail' | 'outlook';
  connection_type?: string;
  email_address?: string;
  is_active: boolean;
  sync_enabled: boolean;
  sync_folders?: string[];
  last_sync_at: string | null;
  last_sync_error?: string | null;
  created_at: string;
}

interface CalendarIntegration {
  id: number;
  provider: 'google_calendar' | 'outlook_calendar';
  is_active: boolean;
  sync_to_crm: boolean;
  last_sync_at: string | null;
}

interface EmailIntegrationSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EmailIntegrationSettings({ isOpen, onClose }: EmailIntegrationSettingsProps) {
  // Bring-your-own OAuth app. Off unless an org specifically asked for it: the standard
  // way to connect a mailbox is the platform Graph flow in Settings → Connected Services.
  // This screen's OAuth path uses per-org credentials and writes a DIFFERENT token store
  // (user_email_integrations, not oauth_tokens), so offering both by default is how users
  // ended up "connected" via one path while the composer read the other and saw nothing.
  const byopOrgId = parseInt(localStorage.getItem('active_cbo_id') || '0', 10) || null;
  const { enabled: byopEnabled, loading: byopLoading } = useCapability(byopOrgId, 'email_oauth_byop');

  const [emailIntegrations, setEmailIntegrations] = useState<EmailIntegration[]>([]);
  const [calendarIntegrations, setCalendarIntegrations] = useState<CalendarIntegration[]>([]);
  const [imapIntegrations, setImapIntegrations] = useState<IMAPIntegration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [syncDaysBack, setSyncDaysBack] = useState(7);
  const [error, setError] = useState<string | null>(null);
  const [showOAuthSetup, setShowOAuthSetup] = useState(false);
  const [showImapConnect, setShowImapConnect] = useState(false);
  const [showFolderPicker, setShowFolderPicker] = useState<number | null>(null);
  const [showOutlookFolderPicker, setShowOutlookFolderPicker] = useState<number | null>(null);

  // Fetch integrations on mount
  useEffect(() => {
    if (isOpen) {
      fetchIntegrations();
    }
  }, [isOpen]);

  const fetchIntegrations = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch email integrations
      const emailResponse = await apiClient.get('/api/crm-activities/email-integrations');
      const allIntegrations = emailResponse.data?.integrations || [];

      // Separate OAuth from IMAP integrations
      setEmailIntegrations(allIntegrations.filter((i: any) => i.connection_type !== 'imap'));
      setImapIntegrations(allIntegrations.filter((i: any) => i.connection_type === 'imap'));

      // Calendar uses the same Outlook OAuth connection — mark as available if Outlook is connected
      const outlookConnected = allIntegrations.some((i: any) => i.provider === 'outlook' && i.is_active);
      if (outlookConnected) {
        setCalendarIntegrations([{ id: 1, provider: 'outlook_calendar', is_active: true, sync_to_crm: true, last_sync_at: null }]);
      } else {
        setCalendarIntegrations([]);
      }
    } catch (err) {
      setError('Failed to load integrations');
      console.error('Failed to fetch integrations:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const connectProvider = async (provider: 'google' | 'microsoft') => {
    const label = provider === 'google' ? 'Gmail' : 'Outlook';
    try {
      // Get the authorization URL from backend (credentials stored in DB, not env vars)
      const response = await apiClient.get(`/api/crm-activities/email-oauth/authorize?provider=${provider}`);
      const { auth_url } = response.data;

      if (!auth_url) {
        setError(`No OAuth URL returned for ${label}. Admin must configure OAuth credentials.`);
        return;
      }

      // Open OAuth popup
      const popup = window.open(auth_url, `Connect ${label}`, 'width=500,height=600,scrollbars=yes');

      // Listen for OAuth callback (backend callback page sends postMessage)
      const messageType = `${provider.toUpperCase()}_OAUTH_SUCCESS`;
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === messageType) {
          window.removeEventListener('message', handleMessage);
          popup?.close();
          // Backend already stored tokens during callback — just refresh
          fetchIntegrations();
        }
      };
      window.addEventListener('message', handleMessage);

      // Cleanup listener after 5 minutes (in case popup closes without callback)
      setTimeout(() => window.removeEventListener('message', handleMessage), 5 * 60 * 1000);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || `Failed to initiate ${label} connection`;
      setError(detail);
    }
  };

  const connectGmail = () => connectProvider('google');
  const connectOutlook = () => connectProvider('microsoft');

  const disconnectIntegration = async (provider: string) => {
    if (!confirm(`Are you sure you want to disconnect ${provider}?`)) {
      return;
    }
    try {
      await apiClient.delete(`/api/crm-activities/email-integrations/${provider}`);
      fetchIntegrations();
    } catch (err) {
      setError(`Failed to disconnect ${provider}`);
    }
  };

  const syncEmails = async (provider: string) => {
    setIsSyncing(provider);
    try {
      const response = await apiClient.post(`/api/crm-activities/email-integrations/${provider}/sync`, null, {
        params: { days_back: syncDaysBack },
      });
      alert(`Synced ${response.data?.synced_count || 0} emails (${syncDaysBack}-day lookback)`);
      fetchIntegrations();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Sync failed');
    } finally {
      setIsSyncing(null);
    }
  };

  const syncCalendar = async () => {
    setIsSyncing('calendar');
    try {
      const response = await apiClient.post('/api/crm-activities/calendar-sync/meetings', null, {
        params: { days_back: syncDaysBack, days_forward: 14 },
      });
      const d = response.data || {};
      if (d.success === false) {
        setError(d.message || 'Calendar sync failed');
      } else {
        alert(`Synced ${d.synced_count || 0} calendar events (${d.skipped || 0} already synced, ${d.total_events || 0} total)`);
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.response?.data?.message || 'Calendar sync failed');
    } finally {
      setIsSyncing(null);
    }
  };

  const toggleSync = async (provider: string, enabled: boolean) => {
    try {
      await apiClient.patch(`/api/crm-activities/email-integrations/${provider}/settings`, { sync_enabled: enabled });
      setEmailIntegrations(prev =>
        prev.map(int =>
          int.provider === provider ? { ...int, sync_enabled: enabled } : int
        )
      );
    } catch (err) {
      console.error('Failed to toggle sync:', err);
    }
  };

  const getProviderIcon = (provider: string) => {
    if (provider === 'gmail' || provider === 'google_calendar') {
      return (
        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
          <Mail className="h-4 w-4 text-red-600" />
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
        <Mail className="h-4 w-4 text-blue-600" />
      </div>
    );
  };

  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case 'gmail':
        return 'Gmail';
      case 'outlook':
        return 'Outlook / Microsoft 365';
      case 'google_calendar':
        return 'Google Calendar';
      case 'outlook_calendar':
        return 'Outlook Calendar';
      default:
        return provider;
    }
  };

  const hasGmail = emailIntegrations.some(i => i.provider === 'gmail' && i.is_active);
  const hasOutlook = emailIntegrations.some(i => i.provider === 'outlook' && i.is_active);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Email & Calendar Integration
          </DialogTitle>
          <DialogDescription>
            Connect your email and calendar to automatically capture activities.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4" />
              {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
              {/* Sync lookback period */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                <span className="text-sm text-gray-600">Sync lookback:</span>
                <select
                  value={syncDaysBack}
                  onChange={(e) => setSyncDaysBack(parseInt(e.target.value))}
                  className="h-8 text-sm border rounded-md px-2 bg-white"
                >
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                </select>
                <span className="text-xs text-gray-400">How far back to fetch emails and calendar events when syncing</span>
              </div>

              {/* Email Integrations */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Email Accounts</h4>
                <div className="space-y-3">
                  {/* Gmail */}
                  {hasGmail ? (
                    <IntegrationCard
                      integration={emailIntegrations.find(i => i.provider === 'gmail')!}
                      onDisconnect={() => disconnectIntegration('gmail')}
                      onSync={() => syncEmails('gmail')}
                      onToggleSync={(enabled) => toggleSync('gmail', enabled)}
                      isSyncing={isSyncing === 'gmail'}
                    />
                  ) : byopEnabled ? (
                    <ConnectButton
                      provider="gmail"
                      label="Connect Gmail"
                      description="Sync emails from your Google account"
                      onClick={connectGmail}
                    />
                  ) : null}

                  {/* Outlook */}
                  {hasOutlook ? (
                    <IntegrationCard
                      integration={emailIntegrations.find(i => i.provider === 'outlook')!}
                      onDisconnect={() => disconnectIntegration('outlook')}
                      onSync={() => syncEmails('outlook')}
                      onToggleSync={(enabled) => toggleSync('outlook', enabled)}
                      onManageFolders={() => {
                        const outlookIntegration = emailIntegrations.find(i => i.provider === 'outlook');
                        if (outlookIntegration) setShowOutlookFolderPicker(outlookIntegration.id);
                      }}
                      isSyncing={isSyncing === 'outlook'}
                    />
                  ) : byopEnabled ? (
                    <ConnectButton
                      provider="outlook"
                      label="Connect Outlook"
                      description="Sync emails from Microsoft 365"
                      onClick={connectOutlook}
                    />
                  ) : null}

                  {/* Standard path. Shown whenever this org has not opted into its own
                      OAuth app, so there is exactly one place to connect a mailbox. */}
                  {!byopLoading && !byopEnabled && !hasGmail && !hasOutlook && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <p className="text-sm font-medium text-gray-900">Connect your mailbox in Settings</p>
                      <p className="mt-1 text-sm text-gray-600">
                        Go to <span className="font-medium">Settings &rarr; Connected Services</span> and connect
                        Microsoft 365 / Outlook. That connection works everywhere &mdash; the member Inbox and
                        sending as yourself from Conversations.
                      </p>
                      <p className="mt-2 text-xs text-gray-500">
                        Using your organization&rsquo;s own OAuth app instead? Ask WeaveIn support to enable it for you.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* IMAP/SMTP Integrations */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Other Email Providers (IMAP/SMTP)</h4>
                <div className="space-y-3">
                  {imapIntegrations.map((integration) => (
                    <ImapIntegrationCard
                      key={integration.id}
                      integration={integration}
                      onSync={async () => {
                        setIsSyncing(`imap-${integration.id}`);
                        try {
                          const result = await imapAPI.sync(integration.id);
                          alert(`Synced ${result?.synced_count || 0} emails`);
                          fetchIntegrations();
                        } catch (err: any) {
                          setError(err?.response?.data?.detail || 'IMAP sync failed');
                        } finally {
                          setIsSyncing(null);
                        }
                      }}
                      onManageFolders={() => setShowFolderPicker(integration.id)}
                      onDisconnect={async () => {
                        if (!confirm(`Disconnect ${integration.email_address}?`)) return;
                        try {
                          await imapAPI.disconnect(integration.id);
                          fetchIntegrations();
                        } catch (err) {
                          setError('Failed to disconnect');
                        }
                      }}
                      isSyncing={isSyncing === `imap-${integration.id}`}
                    />
                  ))}

                  <button
                    onClick={() => setShowImapConnect(true)}
                    className="w-full border border-dashed border-gray-300 rounded-lg p-4 hover:bg-gray-50 text-left transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                        <Server className="h-5 w-5 text-gray-500" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">Connect via IMAP/SMTP</p>
                        <p className="text-sm text-gray-500">
                          Connect any email provider using email and password
                        </p>
                      </div>
                      <Plus className="h-5 w-5 text-gray-400" />
                    </div>
                  </button>
                </div>
              </div>

              {/* IMAP Connect Modal */}
              {showImapConnect && (
                <ImapConnectModal
                  isOpen={showImapConnect}
                  onClose={() => setShowImapConnect(false)}
                  onConnected={(integrationId) => {
                    setShowImapConnect(false);
                    setShowFolderPicker(integrationId);
                    fetchIntegrations();
                  }}
                  onError={(msg) => setError(msg)}
                />
              )}

              {/* Folder Picker Modal */}
              {showFolderPicker !== null && (
                <FolderPickerModal
                  isOpen={showFolderPicker !== null}
                  integrationId={showFolderPicker}
                  onClose={() => setShowFolderPicker(null)}
                  onSaved={() => {
                    setShowFolderPicker(null);
                    fetchIntegrations();
                  }}
                  onError={(msg) => setError(msg)}
                />
              )}

              {/* Outlook Folder Picker Modal */}
              {showOutlookFolderPicker !== null && (
                <OutlookFolderPickerModal
                  isOpen={showOutlookFolderPicker !== null}
                  integrationId={showOutlookFolderPicker}
                  onClose={() => setShowOutlookFolderPicker(null)}
                  onSaved={() => {
                    setShowOutlookFolderPicker(null);
                    fetchIntegrations();
                  }}
                  onError={(msg) => setError(msg)}
                />
              )}

              {/* Calendar Integrations */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Calendar</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Calendar className="h-4 w-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Calendar Sync</p>
                        <p className="text-sm text-gray-500">
                          Sync meetings to CRM activities
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={syncCalendar}
                      disabled={isSyncing === 'calendar'}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {isSyncing === 'calendar' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Sync Now
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    Calendar sync uses your connected Google or Microsoft account.
                    Meetings with external attendees will be automatically associated
                    with opportunities based on email addresses.
                  </p>
                </div>
              </div>

              {/* Admin OAuth Setup — BYOP only. Registering per-org OAuth credentials
                  creates a second, divergent connect path, so it stays hidden unless the
                  org explicitly opted in via the `email_oauth_byop` capability. */}
              <div hidden={!byopEnabled}>
                <button
                  onClick={() => setShowOAuthSetup(!showOAuthSetup)}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                >
                  <Settings className="h-4 w-4" />
                  <span>{showOAuthSetup ? 'Hide' : 'Admin:'} OAuth Credentials Setup</span>
                </button>
                {showOAuthSetup && (
                  <OAuthSetupForm
                    onSaved={() => {
                      setShowOAuthSetup(false);
                      fetchIntegrations();
                    }}
                    onError={(msg) => setError(msg)}
                  />
                )}
              </div>

              {/* How It Works */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  How Activity Capture Works
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Emails sent to/from contacts are automatically logged as activities</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Meetings with external attendees are captured from your calendar</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>Activities are auto-linked to opportunities based on email domains</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span>You can still manually add activities at any time</span>
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Integration Card Component
interface IntegrationCardProps {
  integration: EmailIntegration;
  onDisconnect: () => void;
  onSync: () => void;
  onToggleSync: (enabled: boolean) => void;
  onManageFolders?: () => void;
  isSyncing: boolean;
}

function IntegrationCard({
  integration,
  onDisconnect,
  onSync,
  onToggleSync,
  onManageFolders,
  isSyncing,
}: IntegrationCardProps) {
  const isGmail = integration.provider === 'gmail';

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg flex-shrink-0 ${isGmail ? 'bg-red-100' : 'bg-blue-100'} flex items-center justify-center`}>
            <Mail className={`h-5 w-5 ${isGmail ? 'text-red-600' : 'text-blue-600'}`} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-gray-900">
                {isGmail ? 'Gmail' : 'Outlook'}
              </p>
              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                Connected
              </span>
            </div>
            {integration.email_address && (
              <p className="text-sm text-gray-600 truncate">{integration.email_address}</p>
            )}
            <p className="text-sm text-gray-500">
              {integration.sync_folders && integration.sync_folders.length > 0
                ? `${integration.sync_folders.length} folder${integration.sync_folders.length > 1 ? 's' : ''} · `
                : ''}
              {integration.last_sync_at
                ? `Last synced ${new Date(integration.last_sync_at).toLocaleString()}`
                : 'Never synced'}
            </p>
            {integration.last_sync_error && (
              <p className="text-xs text-red-500 mt-1">Error: {integration.last_sync_error}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {onManageFolders && (
            <button
              onClick={onManageFolders}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Manage sync folders"
            >
              <FolderOpen className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onSync}
            disabled={isSyncing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Sync now"
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={onDisconnect}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
            title="Disconnect"
          >
            <Unlink className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Sync Toggle */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm text-gray-700">Auto-sync emails</span>
          <button
            onClick={() => onToggleSync(!integration.sync_enabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              integration.sync_enabled ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                integration.sync_enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </label>
      </div>
    </div>
  );
}

// Connect Button Component
interface ConnectButtonProps {
  provider: string;
  label: string;
  description: string;
  onClick: () => void;
}

function ConnectButton({ provider, label, description, onClick }: ConnectButtonProps) {
  const isGmail = provider === 'gmail';

  return (
    <button
      onClick={onClick}
      className="w-full border border-gray-200 rounded-lg p-4 hover:bg-gray-50 text-left transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg ${isGmail ? 'bg-red-100' : 'bg-blue-100'} flex items-center justify-center`}>
          <Mail className={`h-5 w-5 ${isGmail ? 'text-red-600' : 'text-blue-600'}`} />
        </div>
        <div className="flex-1">
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <Plus className="h-5 w-5 text-gray-400" />
      </div>
    </button>
  );
}

// IMAP Integration Card Component
interface ImapIntegrationCardProps {
  integration: IMAPIntegration;
  onSync: () => void;
  onManageFolders: () => void;
  onDisconnect: () => void;
  isSyncing: boolean;
}

function ImapIntegrationCard({
  integration,
  onSync,
  onManageFolders,
  onDisconnect,
  isSyncing,
}: ImapIntegrationCardProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
            <Server className="h-5 w-5 text-gray-600" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-gray-900 truncate">{integration.email_address}</p>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                integration.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {integration.is_active ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {integration.provider?.toUpperCase() || 'IMAP'} &middot;{' '}
              {integration.sync_folders?.length || 0} folders &middot;{' '}
              {integration.last_sync_at
                ? `Synced ${new Date(integration.last_sync_at).toLocaleString()}`
                : 'Never synced'}
            </p>
            {integration.last_sync_error && (
              <p className="text-xs text-red-500 mt-1">
                Error: {integration.last_sync_error}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={onManageFolders}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Manage folders"
          >
            <FolderOpen className="h-4 w-4" />
          </button>
          <button
            onClick={onSync}
            disabled={isSyncing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Sync now"
          >
            {isSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </button>
          <button
            onClick={onDisconnect}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
            title="Disconnect"
          >
            <Unlink className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// IMAP Connect Modal
interface ImapConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnected: (integrationId: number) => void;
  onError: (msg: string) => void;
}

function ImapConnectModal({ isOpen, onClose, onConnected, onError }: ImapConnectModalProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [imapUseSsl, setImapUseSsl] = useState(true);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [provider, setProvider] = useState('');
  const [providerHint, setProviderHint] = useState('');
  const [isDetecting, setIsDetecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Auto-detect on email blur
  const handleEmailBlur = async () => {
    if (!email || !email.includes('@')) return;
    setIsDetecting(true);
    setProviderHint('');
    try {
      const result = await imapAPI.detectProvider(email);
      if (result.detected) {
        setImapHost(result.imap_host);
        setImapPort(result.imap_port);
        setImapUseSsl(result.imap_use_ssl);
        setSmtpHost(result.smtp_host);
        setSmtpPort(result.smtp_port);
        setSmtpUseTls(result.smtp_use_tls);
        setProvider(result.provider || '');
        if (result.hint) setProviderHint(result.hint);
      }
    } catch {
      // Detection is best-effort
    } finally {
      setIsDetecting(false);
    }
  };

  const extractErrorMessage = (err: any): string => {
    const detail = err?.response?.data?.detail;
    if (!detail) return 'An error occurred';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ');
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
  };

  const handleTest = async () => {
    if (!email || !password || !imapHost) return;
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await imapAPI.testConnection({
        email, password, imap_host: imapHost, imap_port: imapPort, imap_use_ssl: imapUseSsl,
      });
      setTestResult({ success: result.success, message: result.message || 'Connection successful!' });
    } catch (err: any) {
      setTestResult({ success: false, message: extractErrorMessage(err) || 'Connection failed' });
    } finally {
      setIsTesting(false);
    }
  };

  const handleConnect = async () => {
    if (!email || !password || !imapHost || !smtpHost) {
      onError('Please fill in all required fields');
      return;
    }
    setIsConnecting(true);
    try {
      const result = await imapAPI.connect({
        email, password,
        imap_host: imapHost, imap_port: imapPort, imap_use_ssl: imapUseSsl,
        smtp_host: smtpHost, smtp_port: smtpPort, smtp_use_tls: smtpUseTls,
        provider: provider || undefined,
      });
      onConnected(result.id);
    } catch (err: any) {
      onError(extractErrorMessage(err) || 'Failed to connect');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-gray-600" />
            Connect Email via IMAP/SMTP
          </DialogTitle>
          <DialogDescription>
            Connect any email provider using your email and password (or app password).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {providerHint && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-sm text-amber-800">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{providerHint}</span>
            </div>
          )}

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={handleEmailBlur}
              placeholder="you@example.com"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {isDetecting && (
              <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Detecting server settings...
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password / App Password *</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="App password recommended"
                className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* IMAP Settings */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">IMAP Host *</label>
              <input
                type="text"
                value={imapHost}
                onChange={(e) => setImapHost(e.target.value)}
                placeholder="imap.example.com"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Port</label>
              <input
                type="number"
                value={imapPort}
                onChange={(e) => setImapPort(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {/* SMTP Settings */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">SMTP Host *</label>
              <input
                type="text"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="smtp.example.com"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Port</label>
              <input
                type="number"
                value={smtpPort}
                onChange={(e) => setSmtpPort(Number(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
              testResult.success
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {testResult.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              {testResult.message}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleTest}
            disabled={isTesting || !email || !password || !imapHost}
            className="px-4 py-2 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50"
          >
            {isTesting ? <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> : null}
            Test Connection
          </button>
          <button
            onClick={handleConnect}
            disabled={isConnecting || !email || !password || !imapHost || !smtpHost}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isConnecting ? <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> : null}
            Connect
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Folder Picker Modal
interface FolderPickerModalProps {
  isOpen: boolean;
  integrationId: number;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

function FolderPickerModal({ isOpen, integrationId, onClose, onSaved, onError }: FolderPickerModalProps) {
  const [folders, setFolders] = useState<IMAPFolder[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>(['INBOX', 'Sent']);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && integrationId) {
      loadFolders();
    }
  }, [isOpen, integrationId]);

  const loadFolders = async () => {
    setIsLoading(true);
    try {
      const folderList = await imapAPI.getFolders(integrationId);
      setFolders(folderList);
    } catch (err: any) {
      onError(err?.response?.data?.detail || 'Failed to load folders');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFolder = (name: string) => {
    setSelectedFolders((prev) =>
      prev.includes(name) ? prev.filter((f) => f !== name) : [...prev, name]
    );
  };

  const handleSave = async () => {
    if (selectedFolders.length === 0) {
      onError('Select at least one folder');
      return;
    }
    setIsSaving(true);
    try {
      await imapAPI.updateFolders(integrationId, selectedFolders);
      onSaved();
    } catch (err: any) {
      onError(err?.response?.data?.detail || 'Failed to update folders');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-gray-600" />
            Select Folders to Sync
          </DialogTitle>
          <DialogDescription>
            Choose which email folders to capture as CRM activities.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : folders.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No folders found</p>
          ) : (
            <div className="space-y-1">
              {folders.map((folder) => (
                <label
                  key={folder.name}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedFolders.includes(folder.name)}
                    onChange={() => toggleFolder(folder.name)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300"
                  />
                  <FolderOpen className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-700">{folder.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || selectedFolders.length === 0}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> : null}
            Save Folders
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// OAuth Setup Form (Admin only)
interface OAuthSetupFormProps {
  onSaved: () => void;
  onError: (msg: string) => void;
}

function OAuthSetupForm({ onSaved, onError }: OAuthSetupFormProps) {
  const [provider, setProvider] = useState<'google' | 'microsoft'>('google');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId || !clientSecret) {
      onError('Client ID and Client Secret are required');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/api/crm-activities/email-oauth/clients', {
        provider,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri || null,
        scopes: null,
      });
      setClientId('');
      setClientSecret('');
      setRedirectUri('');
      onSaved();
    } catch (err: any) {
      onError(err?.response?.data?.detail || 'Failed to save OAuth credentials');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="mt-3 p-4 bg-gray-50 rounded-lg space-y-3">
      <p className="text-xs text-gray-500">
        Configure OAuth client credentials for your organization. Get these from the{' '}
        <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          Google Cloud Console
        </a>{' '}
        or{' '}
        <a href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
          Azure App Registrations
        </a>.
      </p>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Provider</label>
        <select
          value={provider}
          onChange={(e) => setProvider(e.target.value as 'google' | 'microsoft')}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
        >
          <option value="google">Google (Gmail)</option>
          <option value="microsoft">Microsoft (Outlook)</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Client ID *</label>
        <input
          type="text"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          required
          placeholder="e.g., 123456789-abc.apps.googleusercontent.com"
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Client Secret *</label>
        <input
          type="password"
          value={clientSecret}
          onChange={(e) => setClientSecret(e.target.value)}
          required
          placeholder="OAuth client secret"
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Redirect URI (optional)</label>
        <input
          type="text"
          value={redirectUri}
          onChange={(e) => setRedirectUri(e.target.value)}
          placeholder="Auto-detected if left blank"
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
        />
      </div>
      <button
        type="submit"
        disabled={saving}
        className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {saving ? 'Saving...' : `Save ${provider === 'google' ? 'Google' : 'Microsoft'} Credentials`}
      </button>
    </form>
  );
}

// Outlook Folder Picker Modal
interface OutlookFolderPickerModalProps {
  isOpen: boolean;
  integrationId: number;
  onClose: () => void;
  onSaved: () => void;
  onError: (msg: string) => void;
}

interface OutlookFolder {
  id: string;
  name: string;
  well_known_id: string | null;
  total_count: number;
  unread_count: number;
}

function OutlookFolderPickerModal({ isOpen, integrationId, onClose, onSaved, onError }: OutlookFolderPickerModalProps) {
  const [folders, setFolders] = useState<OutlookFolder[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>(['inbox', 'sentitems']);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && integrationId) {
      loadFolders();
    }
  }, [isOpen, integrationId]);

  const loadFolders = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get(`/api/crm-activities/outlook/${integrationId}/folders`);
      setFolders(response.data?.folders || []);
      setSelectedFolders(response.data?.selected_folders || ['inbox', 'sentitems']);
    } catch (err: any) {
      onError(err?.response?.data?.detail || 'Failed to load Outlook folders');
    } finally {
      setIsLoading(false);
    }
  };

  const getFolderSyncId = (folder: OutlookFolder): string => {
    // Use well-known ID if available, otherwise use display name
    return folder.well_known_id || folder.name;
  };

  const toggleFolder = (syncId: string) => {
    setSelectedFolders((prev) =>
      prev.includes(syncId) ? prev.filter((f) => f !== syncId) : [...prev, syncId]
    );
  };

  const handleSave = async () => {
    if (selectedFolders.length === 0) {
      onError('Select at least one folder');
      return;
    }
    setIsSaving(true);
    try {
      await apiClient.patch(`/api/crm-activities/outlook/${integrationId}/folders`, {
        folders: selectedFolders,
      });
      onSaved();
    } catch (err: any) {
      onError(err?.response?.data?.detail || 'Failed to update folders');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[420px] max-h-[70vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-blue-600" />
            Outlook Sync Folders
          </DialogTitle>
          <DialogDescription>
            Choose which Outlook folders to sync into your CRM. Emails from selected folders will appear in your Email Inbox.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : folders.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No folders found</p>
          ) : (
            <div className="space-y-1">
              {folders.map((folder) => {
                const syncId = getFolderSyncId(folder);
                const isSelected = selectedFolders.includes(syncId);
                return (
                  <label
                    key={folder.id}
                    className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleFolder(syncId)}
                      className="w-4 h-4 text-blue-600 rounded border-gray-300"
                    />
                    <FolderOpen className={`h-4 w-4 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-gray-700">{folder.name}</span>
                      <span className="ml-2 text-xs text-gray-400">
                        {folder.total_count} emails
                        {folder.unread_count > 0 && ` · ${folder.unread_count} unread`}
                      </span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || selectedFolders.length === 0}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> : null}
            Save Folders ({selectedFolders.length} selected)
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EmailIntegrationSettings;
